import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import theme from '../theme'
import { FONT } from './board'
import { useClock } from '../utils/clock'
import { pathForEvent } from '../utils/routes'
import Favorite from './Favorite'
import Ladders from './Ladders'

/**
 * One fixture as a card: who is playing, when, and one book's ladders under it. The favourites board is
 * made of these, and it hands over the handlers that let a card be carried around.
 *
 * The card is as wide as what is on it and no wider. A fixed width made every card as wide as the widest
 * thing on the screen, and the point of this layout is to get as many games in front of somebody at once
 * as the screen will hold.
 */

export const markSx = { color: '#9A9EA3', fontSize: '11px' }

const cardSx = {
  flex: '0 0 auto',
  maxWidth: '100%',
  border: '1px solid #E4E4E8',
  borderRadius: '2px',
  backgroundColor: 'white',
  padding: '10px 12px',
  fontFamily: FONT,
  '&:hover': { borderColor: '#C9C9D1' }
}

function LadderCard({ card, game, loading, markets, lines, usPrices, onPrice, book, drag, sx, expanded, onExpand, movedOf }) {
  const clock = useClock()
  const navigate = useNavigate()
  const open = () => navigate(`${pathForEvent(card)}?book=pinnacle`)

  /**
   * Quiet: the match is under way, the feed has gone silent about this game, or the book is not pricing
   * it just now. In every one of those the numbers on the card are not something anybody can take, so
   * the card says so by fading rather than by disappearing out from under the reader.
   */
  const quiet = Boolean(card.started || (!game && !loading))

  /**
   * Whether there is anything left to open up. A fight has one line per market and a card offering to
   * show more of it is a link that does nothing; only the markets actually on the card are counted, so
   * switching team totals off does not leave the offer behind.
   */
  const deeper = (rows) => (rows || []).length > lines
  const more = Boolean(game) && (
    (markets.includes('spread') && deeper(game.spreads))
    || (markets.includes('total') && deeper(game.totals))
    || (markets.includes('tt') && (deeper(game.homeTeamTotals) || deeper(game.awayTeamTotals)))
  )

  return (
    <Box
      {...(drag || {})}
      sx={{
        ...cardSx,
        cursor: drag ? 'grab' : 'default',
        color: quiet ? '#9A9EA3' : 'inherit',
        backgroundColor: quiet ? '#FAFAFB' : 'white',
        ...(sx || {})
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
        <Favorite compact event={card} />
        <Box
          component="span"
          onClick={open}
          sx={{
            flexGrow: 1, minWidth: 0, maxWidth: '260px', fontSize: '13px', fontWeight: 700, cursor: 'pointer',
            color: quiet ? '#9A9EA3' : 'inherit',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            '&:hover': { textDecoration: 'underline', color: theme.palette.primary.light }
          }}
        >
          {card.home} v {card.away}
        </Box>
        <Typography
          title={card.started
            ? `Started ${clock.kickOff(card.koTime)} — the last prices before the off, nobody is taking these`
            : undefined}
          sx={{ ...markSx, whiteSpace: 'nowrap' }}
        >
          {card.started ? 'In play' : clock.kickOff(card.koTime)}
        </Typography>
      </Box>

      <Typography sx={{ ...markSx, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '6px' }}>
        {card.league}
      </Typography>

      {game ? (
        <Ladders
          game={game}
          markets={markets}
          // opened up, the card shows every rung the book prices rather than the main line and its
          // neighbours — the same columns, just longer
          lines={expanded ? Number.MAX_SAFE_INTEGER : lines}
          usPrices={usPrices}
          dim={quiet}
          onPrice={onPrice}
          movedOf={movedOf}
        />
      ) : (
        <Typography sx={{ ...markSx, padding: '4px 0' }}>
          {loading ? 'Loading…' : `No ${book} lines`}
        </Typography>
      )}

      {/* every rung of every ladder, in the card rather than on another screen: reading a match should
          not cost losing your place on the board */}
      {(expanded || more || !onExpand) && (
        <Box
          component="span"
          onClick={onExpand ? () => onExpand(card.key) : open}
          sx={{
            display: 'inline-block', marginTop: '6px', fontSize: '11px', color: '#9A9EA3', cursor: 'pointer',
            '&:hover': { color: theme.palette.primary.light, textDecoration: 'underline' }
          }}
        >
          {expanded ? 'Fewer lines' : 'Show all lines'}
        </Box>
      )}
    </Box>
  )
}

export default LadderCard
